import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { plainToClass } from 'class-transformer';
import { catchError, map, Observable, of } from 'rxjs';

import { ResearchType } from '@empathy/global/data';
import { SurveyModel } from '@empathy/survey/base';

import { EnvironmentConfig } from '../config/environment.config';
import { SaveAIProjectTemplateInput } from '../models/research-templates/ai-interview-template.model';
import { AIInterviewTemplate } from '../models/research-templates/ai-interview-template.model';
import { IResearchTemplate, SaveSurveyProjectTemplateInput } from '../models/research-templates/research-template.interface';
import { SurveyTemplate } from '../models/research-templates/survey-template.model';

@Injectable({
  providedIn: 'root'
})
export class ResearchTemplatesService {
  constructor(
    @Inject('environment') private environment: EnvironmentConfig,
    private http: HttpClient
  ) {}

  getResearchTemplates(projectId: string, researchMethodId: string): Observable<IResearchTemplate[] | AIInterviewTemplate[]> {
    return this.http
      .get<IResearchTemplate[] | AIInterviewTemplate[]>(`${this.environment.endPoint}project/${projectId}/research/${researchMethodId}/templates`)
      .pipe(
        catchError(() => of([])),
        map((templates) => {
          if (templates?.length) {
            templates.forEach((template) => {
              if (template.surveyJson) {
                const model = plainToClass(SurveyModel, JSON.parse(template.surveyJson));
                template.survey = model;
              }
            });
          }

          return templates;
        })
      );
  }

  getTemplateById(templateId: string): Observable<IResearchTemplate | AIInterviewTemplate> {
    return this.http
      .get<IResearchTemplate | AIInterviewTemplate>(`${this.environment.endPoint}research/templates/${templateId}`)
      .pipe(catchError(() => of(null)));
  }

  getTemplatesByResearchType(type: ResearchType): Observable<IResearchTemplate[] | AIInterviewTemplate[]> {
    return this.http
      .get<SurveyTemplate[] | AIInterviewTemplate[]>(`${this.environment.endPoint}research/templates?researchType=${type}`)
      .pipe(catchError(() => of()));
  }

  saveProjectResearchMethodTemplate(projectId: string, researchMethodId: string, templateId: string, notepadInd: boolean): Observable<boolean> {
    const url = `${this.environment.endPoint}project/${projectId}/method/${researchMethodId}/template/convert?templateId=${templateId}&notepadInd=${notepadInd}`;

    return this.http.post<boolean>(url, { templateId }).pipe(catchError(() => of()));
  }

  saveSurveyProjectTemplate(template: SaveSurveyProjectTemplateInput): Observable<boolean> {
    const url = `${this.environment.endPoint}research/templates/survey`;
    return this.http.post<boolean>(url, template);
  }

  saveAIProjectTemplate(template: SaveAIProjectTemplateInput): Observable<boolean> {
    const url = `${this.environment.endPoint}research/templates/ai-interview`;
    return this.http.post<boolean>(url, template);
  }
}
